/**
 * SnapTrade Sync and Conflict Resolution Hooks
 *
 * React Query hooks for syncing account data, refreshing holdings/transactions,
 * and resolving conflicts from broker data mismatches. Everything a sync can
 * change is invalidated through the shared map's `sync` domain (WEBD-07).
 */

import type { TransactionType } from '@luminaria/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { incomeApi, type IncomeConflictResolution } from '../../api/incomeApi';
import { invalidate } from '../../api/invalidation';
import { mutationKeys } from '../../api/queryKeys';
import { snaptradeApi, type TransactionConflictResolution } from '../../api/snaptradeApi';

/**
 * Sync a specific account
 */
export function useSyncAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mutationKeys.syncAccount,
    mutationFn: (accountId: string) => snaptradeApi.syncAccount(accountId),
    onSuccess: () => { void invalidate(queryClient, 'sync'); },
    // Errors also produce sync_error messages and set lastSyncStatus
    onError: () => { void invalidate(queryClient, 'sync'); },
  });
}

/**
 * Trigger daily sync - syncs all connected accounts if not already synced today
 * Used for automatic background sync on first login of day
 */
export function useDailySync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => snaptradeApi.dailySync(),
    onSuccess: (data) => {
      // Only invalidate queries if sync actually ran
      if (data.synced) {
        void invalidate(queryClient, 'sync');
      }
    },
  });
}

/**
 * Force refresh a brokerage connection.
 * This tells SnapTrade to fetch fresh data from the brokerage (not cached).
 * Use this when cached data is stale or missing recent changes.
 */
export function useRefreshConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mutationKeys.refreshConnection,
    mutationFn: (connectionId: string) => snaptradeApi.refreshConnection(connectionId),
    onSuccess: () => { void invalidate(queryClient, 'sync'); },
  });
}

/**
 * Force refresh transactions for a brokerage connection (1 year lookback)
 * Fetches transactions from the past year, regardless of lastSyncAt.
 * `accountId` narrows the refresh to one account.
 */
export function useRefreshTransactions() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: mutationKeys.refreshTransactions,
    mutationFn: (input: { connectionId: string; accountId?: string }) => snaptradeApi.refreshTransactions(input),
    onSuccess: () => { void invalidate(queryClient, 'sync'); },
  });
}

/**
 * Batch resolve multiple transaction conflicts in a single request.
 */
export function useResolveTransactionConflictsBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conflicts: TransactionConflictResolution[]) => snaptradeApi.resolveTransactionConflictsBatch(conflicts),
    onSuccess: () => { void invalidate(queryClient, ['transactions', 'messages']); },
  });
}

/**
 * Batch resolve multiple income record conflicts in a single request.
 */
export function useResolveIncomeConflictsBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conflicts: IncomeConflictResolution[]) => incomeApi.resolveConflictsBatch(conflicts),
    onSuccess: () => { void invalidate(queryClient, 'incomeRecords'); },
  });
}

/**
 * Update option transaction classification
 * Used when user manually corrects an option action (buy_to_open, sell_to_close, etc.)
 */
export function useUpdateOptionClassification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: { accountId: string; externalId: string; action: TransactionType }) =>
      snaptradeApi.updateOptionClassification(input),
    // A reclassified option changes its account's transactions and everything
    // derived from them (income, lots, tax) plus the sync/conflict state.
    onSuccess: () => { void invalidate(queryClient, 'sync'); },
  });
}
